import { getEclassDataById } from "@oneapp/data";
import { ROUTESWEB } from "@repo/utils";
import type { ModuleHandler } from "../types";
import { useAuthStore } from "../../../store/useAuthStore";
import type { Program } from "../../../pages/liveclasses/types";

function findProgramById(targetId?: string): Program | undefined {
  if (!targetId) return undefined;
  const target = String(targetId).trim();
  if (!target) return undefined;

  const enrolledPrograms = useAuthStore.getState().enrolledPrograms as
    | Program[]
    | undefined;

  return enrolledPrograms?.find((program) => {
    if (String(program.program_id) === target) return true;

    const liveClassCohortIds = program.batch?.cohorts?.live_class_cohort_ids;
    if (
      Array.isArray(liveClassCohortIds) &&
      liveClassCohortIds.some((id) => String(id) === target)
    ) {
      return true;
    }

    const examCohortIds = program.batch?.cohorts?.exam_cohort_ids;
    if (
      Array.isArray(examCohortIds) &&
      examCohortIds.some((id) => String(id) === target)
    ) {
      return true;
    }

    if (
      Array.isArray(program.programOfferingIds) &&
      program.programOfferingIds.some((id) => String(id) === target)
    ) {
      return true;
    }

    return false;
  });
}

function normalizeClassStatus(classData: any): string {
  const meetingStatus = String(
    classData?.meetingStatus ?? classData?.meeting?.status ?? "",
  )
    .trim()
    .toLowerCase();
  if (meetingStatus) return meetingStatus;

  if (classData?.isClassEnded === true) return "ended";

  const classSubStatus = String(classData?.classSubStatus ?? "")
    .trim()
    .toLowerCase();
  if (classSubStatus === "recorded") return "ended";

  const rawStatus = String(classData?.status ?? "")
    .trim()
    .toLowerCase();
  if (rawStatus) return rawStatus;

  return String(classData?.classSource?.meetingType ?? "")
    .trim()
    .toLowerCase();
}

function normalizeClassForJoin(rawClass: any) {
  const id = String(rawClass?.id ?? rawClass?._id ?? "").trim();
  if (!id) return null;

  const subjectId =
    rawClass?.subjectID ??
    rawClass?.subjectId ??
    rawClass?.subject_id ??
    rawClass?.subjectMeta?.[0]?.subjectId ??
    "";

  const teacherName = String(rawClass?.teacherName ?? "").trim();
  const teacherParts = teacherName ? teacherName.split(" ") : [];

  return {
    ...rawClass,
    id,
    subjectID: String(subjectId || ""),
    teacher: rawClass?.teacher ?? {
      firstName: teacherParts[0] ?? "",
      lastName: teacherParts.slice(1).join(" "),
      photo: rawClass?.teacherPhoto ?? "",
    },
  };
}

export const liveClassHandler: ModuleHandler = async (parsed, navigate) => {
  const allParams = parsed.allParams || {};
  const { type } = allParams;
  const classid = parsed.classId || allParams.classid;
  const courseId = parsed.courseId || allParams.courseId || allParams.courseid;
  const batchid = parsed.batchId || allParams.batchId || allParams.batchid;

  // 1. Direct Class / Video Player Landing
  if (classid) {
    try {
      const result = (await getEclassDataById(
        { id: classid, fields: "" },
        { clientName: "liveclass" },
      )) as any;
      const rawData = result?.data;
      const classData = rawData?.data ?? rawData;

      if (!classData || typeof classData !== "object" || !classData.id) {
        navigate(`/${ROUTESWEB.APP.COURSES}`, { replace: true });
        return;
      }

      const status = normalizeClassStatus(classData);

      // 1. Ended/Recorded class -> Rewatch
      if (status === "ended" || status === "recorded") {
        navigate(`/live-class/${classid}/rewatch`, {
          replace: true,
          state: { item: classData },
        });
        return;
      }

      const classItem = normalizeClassForJoin(classData);
      if (!classItem) {
        navigate(`/${ROUTESWEB.APP.COURSES}`, { replace: true });
        return;
      }

      const meetingId = classItem?.meeting?.id;
      const classSource = classItem?.classSource?.type;

      // 2. Agora Live Classroom
      if (classSource === "agora" && meetingId != null) {
        navigate(`/live-class/${classid}/student-live-class/${meetingId}`, {
          replace: true,
          state: { item: classItem },
        });
        return;
      }

      // 3. YouTube Live Class
      if (classSource === "youtube") {
        navigate("/live-classes/yt-class", {
          replace: true,
          state: {
            className: classItem?.name,
            url: classItem?.classSource?.url,
            chatChannelName: (classItem as any)?.chatChannelName,
          },
        });
        return;
      }

      // Default fallback: Rewatch
      navigate(`/live-class/${classid}/rewatch`, {
        replace: true,
        state: { item: classItem },
      });
    } catch (error) {
      console.error("[liveClassHandler] Failed to fetch eclass data:", error);
      navigate(`/${ROUTESWEB.APP.COURSES}`, { replace: true });
    }
    return;
  }

  // 2. Course Overview Page (Verify Enrollment)
  const targetId = courseId || batchid;
  if (targetId) {
    const program = findProgramById(targetId);
    if (program) {
      navigate(`/${ROUTESWEB.APP.COURSE_OVERVIEW}/${program.program_id}`, {
        replace: true,
        state: { program },
      });
      return;
    }

    // If not found in enrollment, fallback to Courses home
    navigate(`/${ROUTESWEB.APP.COURSES}`, { replace: true });
    return;
  }

  // 3. Free Classes (Masterclasses) — past / upcoming / ongoing tabs
  const normalizedType = type?.toLowerCase();
  if (
    normalizedType === "past" ||
    normalizedType === "upcoming" ||
    normalizedType === "ongoing"
  ) {
    const activeTab = normalizedType === "past" ? "past" : "upcoming";
    const state = {
      programIds: [], // ClassesList internal logic will fetch defaults if empty
      activeTab,
      subjectFilter: "",
    };
    localStorage.setItem("classesListState", JSON.stringify(state));
    navigate(`/${ROUTESWEB.APP.CLASSES}`, { replace: true });
    return;
  }

  // Default: Live Classes Homepage (mounted at /courses)
  return {
    path: `/${ROUTESWEB.APP.COURSES}`,
  };
};
